import type {
  Shape,
  WallPathShape,
  WallShape
} from "@gettactic/map-builder/src/types";
import { computeWallFloorPolygons } from "@gettactic/map-builder/src/utils/wallFloorFill";
import { findWallJunctions } from "@gettactic/map-builder/src/utils/wallJunction";
import { getAllWallSegments } from "@gettactic/map-builder/src/utils/wallPathSegments";
import React from "react";
import { orderedShapes } from "../document/serialize";
import { useEditorSelector } from "../store/EditorStoreContext";

interface WallSegmentsState {
  walls: Shape[];
  segments: WallShape[];
}

const isWall = (shape: Shape): boolean =>
  shape.type === "wall" || shape.type === "wall-path";

/**
 * The wall shapes and their segments, recomputed only when a wall itself
 * changed. The store keeps untouched shapes by reference, so moving a
 * desk leaves every wall identical and the room fill and junctions
 * (both O(walls²)) are not derived again on that commit.
 */
const useWallSegments = (): WallSegmentsState => {
  const shapes = useEditorSelector((state) => state.shapes);
  const order = useEditorSelector((state) => state.order);
  const previous = React.useRef<WallSegmentsState | null>(null);
  return React.useMemo(() => {
    const walls = orderedShapes({ shapes, order }).filter(isWall);
    const last = previous.current;
    if (
      last &&
      last.walls.length === walls.length &&
      last.walls.every((wall, index) => wall === walls[index])
    ) {
      return last;
    }
    const next = { walls, segments: getAllWallSegments(walls) };
    previous.current = next;
    return next;
  }, [shapes, order]);
};

/** White fill of rooms enclosed by walls. Derived on commit, never during
 *  a gesture, so it is one of the few subscribers to the whole shapes map. */
export const WallFloorFill: React.FC = () => {
  const { segments } = useWallSegments();
  const polygons = React.useMemo(
    () => (segments.length < 3 ? [] : computeWallFloorPolygons(segments)),
    [segments]
  );
  return (
    <g data-layer="wall-floor-fill">
      {polygons.map((polygon, index) => (
        <polygon
          // biome-ignore lint/suspicious/noArrayIndexKey: polygons are derived and unnamed
          key={index}
          points={polygon.points
            .map((point) => `${point.x},${point.y}`)
            .join(" ")}
          fill="white"
        />
      ))}
    </g>
  );
};

/** Corner fills where walls meet, skipping vertices that draw a rounded arc. */
export const WallJunctions: React.FC = () => {
  const { walls, segments } = useWallSegments();
  const junctions = React.useMemo(() => {
    if (segments.length < 2) return [];
    const rounded = walls
      .filter((shape): shape is WallPathShape => shape.type === "wall-path")
      .flatMap((path) =>
        path.vertices.filter((vertex) => (vertex.cornerRadius ?? 0) > 0)
      );
    return findWallJunctions(segments).filter(
      (junction) =>
        !rounded.some(
          (vertex) =>
            Math.abs(vertex.x - junction.center.x) < 2 &&
            Math.abs(vertex.y - junction.center.y) < 2
        )
    );
  }, [walls, segments]);
  return (
    <g data-layer="wall-junctions">
      {junctions.map((junction, index) => (
        <polygon
          // biome-ignore lint/suspicious/noArrayIndexKey: junctions are derived and unnamed
          key={index}
          points={junction.points
            .map((point) => `${point.x},${point.y}`)
            .join(" ")}
          fill={junction.color}
        />
      ))}
    </g>
  );
};
